import profileData from "@/lib/tool-kit/vocabulary/language-profiles.json";

/**
 * The Language Detector: script first, then character trigrams.
 *
 * **Script.** Every letter is assigned its Unicode script, and the script with the most
 * letters wins. Thirteen scripts belong to essentially one language here — Greek,
 * Hebrew, Armenian, Georgian, Thai, Hangul, kana, Ethiopic and five Indic scripts — so
 * the script is the answer. Kana anywhere among Chinese characters means Japanese;
 * Hangul means Korean; Chinese characters alone mean Chinese, and a short list of
 * characters that differ between the two writing systems tells Simplified from
 * Traditional.
 *
 * **Trigrams.** Latin, Cyrillic, Arabic and Devanagari are shared by several languages,
 * which the profiles in `language-profiles.json` separate (built by
 * `scripts/build-language-profiles.mjs` from translations of the Universal Declaration
 * of Human Rights). Each profile is a language's most frequent character trigrams, in
 * rank order (Cavnar & Trenkle 1994). The text's trigrams are scored against every
 * profile of its script: a trigram at rank r counts as log(1 / (r + offset)), Zipf's
 * law, a trigram missing from the profile as a fixed low value, the sum is divided by
 * the temperature (each letter appears in three overlapping trigrams, so the evidence
 * is not independent) and a prior favouring widely used languages is added. A softmax
 * turns the scores into the probabilities shown, which were calibrated on held-out
 * text so that "80%" is right about four times in five.
 */

export type Script =
  | "Latn"
  | "Cyrl"
  | "Grek"
  | "Arab"
  | "Hebr"
  | "Deva"
  | "Beng"
  | "Guru"
  | "Gujr"
  | "Taml"
  | "Thai"
  | "Geor"
  | "Armn"
  | "Ethi"
  | "Hang"
  | "Jpan"
  | "Hani";

type Counted = Exclude<Script, "Jpan"> | "Kana";

/** Commonest first, so most letters match on the first test or two. */
const SCRIPT_TESTS: readonly [Counted, RegExp][] = [
  ["Latn", /\p{Script=Latin}/u],
  ["Cyrl", /\p{Script=Cyrillic}/u],
  ["Hani", /\p{Script=Han}/u],
  ["Arab", /\p{Script=Arabic}/u],
  ["Deva", /\p{Script=Devanagari}/u],
  ["Kana", /[\p{Script=Hiragana}\p{Script=Katakana}]/u],
  ["Hang", /\p{Script=Hangul}/u],
  ["Grek", /\p{Script=Greek}/u],
  ["Hebr", /\p{Script=Hebrew}/u],
  ["Thai", /\p{Script=Thai}/u],
  ["Beng", /\p{Script=Bengali}/u],
  ["Taml", /\p{Script=Tamil}/u],
  ["Gujr", /\p{Script=Gujarati}/u],
  ["Guru", /\p{Script=Gurmukhi}/u],
  ["Geor", /\p{Script=Georgian}/u],
  ["Armn", /\p{Script=Armenian}/u],
  ["Ethi", /\p{Script=Ethiopic}/u],
];

/** Scripts that decide the language on their own. */
export const SCRIPT_LANGUAGES: Readonly<Partial<Record<Script, string>>> = {
  Grek: "el",
  Hebr: "he",
  Armn: "hy",
  Geor: "ka",
  Thai: "th",
  Hang: "ko",
  Jpan: "ja",
  Hani: "zh",
  Ethi: "am",
  Beng: "bn",
  Taml: "ta",
  Gujr: "gu",
  Guru: "pa",
};

const RE_LETTER = /\p{L}/u;

/** Letters per script. Marks, digits, spaces and punctuation are not counted. */
export function scriptCounts(text: string): Map<Counted, number> {
  const counts = new Map<Counted, number>();
  for (const char of text) {
    if (!RE_LETTER.test(char)) {
      continue;
    }
    const found = SCRIPT_TESTS.find(([, test]) => test.test(char));
    if (found) {
      counts.set(found[0], (counts.get(found[0]) ?? 0) + 1);
    }
  }
  return counts;
}

/**
 * The script most of the letters are in, with its share. Hangul, kana and Chinese
 * characters count together, because Japanese and Korean text mix them.
 */
export function dominantScript(
  counts: ReadonlyMap<Counted, number>
): { script: Script; share: number; letters: number } | null {
  const hangul = counts.get("Hang") ?? 0;
  const kana = counts.get("Kana") ?? 0;
  const han = counts.get("Hani") ?? 0;
  let letters = 0;
  let best: { script: Script; size: number } | null = null;
  for (const [script, size] of counts) {
    letters += size;
    if (
      script !== "Hang" &&
      script !== "Kana" &&
      script !== "Hani" &&
      (!best || size > best.size)
    ) {
      best = { script, size };
    }
  }
  const cjk = hangul + kana + han;
  if (cjk > 0 && (!best || cjk > best.size)) {
    let script: Script = "Hani";
    if (hangul > 0 && hangul >= kana) {
      script = "Hang";
    } else if (kana > 0) {
      script = "Jpan";
    }
    best = { script, size: cjk };
  }
  return best && letters > 0
    ? { script: best.script, share: best.size / letters, letters }
    : null;
}

/* --------------------------------------------------------------- Chinese */

const SIMPLIFIED =
  "这们说为国时会来对发经现学过关动进实还长门问间题东车头马书见开让话语应该样写读电业从个机与气么华网体区图";
const TRADITIONAL =
  "這們說為國時會來對發經現學過關動進實還長門問間題東車頭馬書見開讓話語應該樣寫讀電業從個機與氣麼華網體區圖";

/** zh-Hans, zh-Hant, or plain zh when the text gives no sign either way. */
export function chineseVariant(text: string): "zh-Hans" | "zh-Hant" | "zh" {
  let simplified = 0;
  let traditional = 0;
  for (const char of text) {
    if (SIMPLIFIED.includes(char)) {
      simplified += 1;
    } else if (TRADITIONAL.includes(char)) {
      traditional += 1;
    }
  }
  if (simplified === traditional) {
    return "zh";
  }
  return simplified > traditional ? "zh-Hans" : "zh-Hant";
}

/* -------------------------------------------------------------- trigrams */

const RE_WORD = /[\p{L}\p{M}]+/gu;
const RE_CEDILLA_S = /ş/g;
const RE_CEDILLA_T = /ţ/g;

/**
 * The text's trigrams, counted. Must match `trigramCounts` in the build script:
 * NFC, lower case, s- and t-cedilla folded into the comma-below letters Romanian
 * officially uses, words of letters and marks padded as `_word_`.
 */
export function trigramCounts(text: string): Map<string, number> {
  const normalized = text
    .normalize("NFC")
    .toLowerCase()
    .replace(RE_CEDILLA_S, "ș")
    .replace(RE_CEDILLA_T, "ț");
  const counts = new Map<string, number>();
  for (const [word] of normalized.matchAll(RE_WORD)) {
    const chars = Array.from(`_${word}_`);
    for (let index = 0; index + 3 <= chars.length; index += 1) {
      const gram = chars.slice(index, index + 3).join("");
      counts.set(gram, (counts.get(gram) ?? 0) + 1);
    }
  }
  return counts;
}

interface Profile {
  code: string;
  script: Script;
  ranks: Map<string, number>;
  logPrior: number;
}

/** A profile string is its trigrams run together, three code points each. */
export function profileTrigrams(packed: string): string[] {
  const chars = Array.from(packed);
  const out: string[] = [];
  for (let index = 0; index + 3 <= chars.length; index += 3) {
    out.push(chars.slice(index, index + 3).join(""));
  }
  return out;
}

function scriptOfProfile(trigrams: readonly string[]): Script {
  for (const gram of trigrams) {
    for (const char of gram) {
      const found = SCRIPT_TESTS.find(([, test]) => test.test(char));
      if (found && found[0] !== "Kana") {
        return found[0];
      }
    }
  }
  return "Latn";
}

let profiles: Profile[] | null = null;

/** The profiles, parsed on first use. */
export function languageProfiles(): readonly Profile[] {
  if (!profiles) {
    const major = new Set(profileData.major.split(" "));
    profiles = Object.entries(
      profileData.profiles as Record<string, string>
    ).map(([code, packed]) => {
      const trigrams = profileTrigrams(packed);
      return {
        code,
        script: scriptOfProfile(trigrams),
        ranks: new Map(trigrams.map((gram, rank) => [gram, rank])),
        logPrior: Math.log(major.has(code) ? 1 : profileData.minorPrior),
      };
    });
  }
  return profiles;
}

export interface LanguageScore {
  /** A BCP 47 code: "de", "pt", "zh-Hant". */
  code: string;
  probability: number;
}

const HARMONIC = (() => {
  let sum = 0;
  for (let rank = 0; rank < profileData.trigrams; rank += 1) {
    sum += 1 / (rank + profileData.zipfOffset);
  }
  return sum;
})();

const LOG_UNSEEN =
  Math.log(1 / (profileData.trigrams + profileData.zipfOffset) / HARMONIC) -
  profileData.unseenPenalty;

/** Score text against a set of profiles; probabilities sum to 1, best first. */
export function scoreTrigrams(
  counts: ReadonlyMap<string, number>,
  candidates: readonly Profile[]
): LanguageScore[] {
  const totals = candidates.map((profile) => {
    let total = 0;
    for (const [gram, count] of counts) {
      const rank = profile.ranks.get(gram);
      const logP =
        rank === undefined
          ? LOG_UNSEEN
          : Math.log(1 / (rank + profileData.zipfOffset) / HARMONIC);
      total += count * logP;
    }
    return total / profileData.temperature + profile.logPrior;
  });
  const best = Math.max(...totals);
  const weights = totals.map((total) => Math.exp(total - best));
  const sum = weights.reduce((a, b) => a + b, 0);
  return candidates
    .map((profile, index) => ({
      code: profile.code,
      probability: weights[index] / sum,
    }))
    .sort((a, b) => b.probability - a.probability);
}

/* ------------------------------------------------------------- detection */

export interface Detection {
  /** Letters counted, in every script. */
  letters: number;
  script: Script;
  /** Share of the letters in that script, 0–1. */
  scriptShare: number;
  method: "script" | "trigrams";
  /** Best first. One entry, with probability 1, when the script decided. */
  candidates: LanguageScore[];
  /** How many languages the trigram step chose between (0 for script). */
  compared: number;
}

/** The most likely language of `text`, or null when it has no letters. */
export function detectLanguage(text: string): Detection | null {
  const dominant = dominantScript(scriptCounts(text));
  if (!dominant) {
    return null;
  }
  const base = {
    letters: dominant.letters,
    script: dominant.script,
    scriptShare: dominant.share,
  };
  const single = SCRIPT_LANGUAGES[dominant.script];
  if (single) {
    const code = single === "zh" ? chineseVariant(text) : single;
    return {
      ...base,
      method: "script",
      candidates: [{ code, probability: 1 }],
      compared: 0,
    };
  }
  const candidates = languageProfiles().filter(
    (profile) => profile.script === dominant.script
  );
  return {
    ...base,
    method: "trigrams",
    candidates: scoreTrigrams(trigramCounts(text), candidates),
    compared: candidates.length,
  };
}

/** Every language the detector can name, as BCP 47 codes. */
export function supportedLanguages(): string[] {
  return [
    ...languageProfiles().map((profile) => profile.code),
    ...Object.values(SCRIPT_LANGUAGES),
  ].sort();
}
